/**
 * @fileoverview Application configuration constants and endpoints.
 * This module provides centralized configuration for API URLs, authentication endpoints,
 * authorization headers, and payment integration settings across different environments.
 */

import { env } from "./environmentHelpers";

// =============================================================================
// BASE CONFIGURATION
// =============================================================================

/**
 * Root API URL based on the current environment.
 */
export const API_ROOT: string = env.isProd
  ? "https://connect.digitalrepublic.ch"
  : "https://drint-csc2.dev.aax2.net";

// =============================================================================
// AUTHENTICATION API ENDPOINTS
// =============================================================================
/**
 * Base URL for the authentication API server.
 */
export const AUTH_API_URL: URL = new URL("/api-auth-server", API_ROOT);

/**
 * OAuth authorization endpoint for initiating authentication flows.
 */
export const AUTHORIZE_URL: URL = new URL(
  AUTH_API_URL.pathname + "/oauth/authorize",
  API_ROOT,
);

/**
 * OAuth token endpoint for exchanging authorization codes for access tokens.
 */
export const TOKEN_URL: URL = new URL(
  AUTH_API_URL.pathname + "/oauth/token",
  API_ROOT,
);

/**
 * Agent login endpoint for specialized agent authentication.
 */
export const AGENT_LOGIN_URL: URL = new URL(
  AUTH_API_URL.pathname + "/user/agentlogin",
  API_ROOT,
);

/**
 * Password reset endpoint for forgotten password flows.
 */
export const FORGOT_PASSWORD_URL: URL = new URL(
  AUTH_API_URL.toString() + "/passwordReset",
);

// =============================================================================
// FRONTEND API ENDPOINTS
// =============================================================================

/**
 * Base URL for the frontend API (version 2).
 */
export const FRONTEND_API_URL = new URL("/frontend-api-v2", API_ROOT);

/**
 * Callback URL for Datatrans payment processing integration.
 */
export const DATATRANS_CALLBACK_URL = new URL(
  FRONTEND_API_URL.pathname + "/datatrans/callback",
  API_ROOT,
);

// =============================================================================
// AUTHORIZATION HEADERS
// =============================================================================

/**
 * Basic authorization header for portal access.
 */
export const BASIC_PORTAL_AUTHORIZATION: string = `Basic ${btoa("portal:portal")}`;

/**
 * Basic authorization header for BOTH the public customer and partner.
 */
export const PUBLIC_AUTHORIZATION: string = `Basic ${btoa(
  "digital-republic-public:",
)}`;

/**
 * Basic authorization header for authenticated customer access.
 */
export const CUSTOMER_AUTHORIZATION: string = `Basic ${btoa("digital-republic:")}`;

/**
 * Basic authorization header for authenticated partner access.
 */
export const PARTNER_AUTHORIZATION = `Basic ${btoa("digital-republic-public:")}`;
